import { createServerFn } from "@tanstack/react-start";
import { redirect } from "@tanstack/react-router";
import { getAuth } from "@clerk/tanstack-react-start/server";
import { getWebRequest } from "@tanstack/react-start/server";
import type { OnboardingStatusResponse } from "@/lib/api/generated";
import { readOnboardingStatusApiOnboardingStatusGet } from "@/lib/api/generated/onboarding/onboarding";
import { logInfo, logWarn } from "@/lib/structured-log";
import { extractApiErrorStatus, isAuthError } from "@/lib/api/error";
import { executeStateMachineRedirect } from "@/lib/routing/onboarding-redirect";
import { buildSigninRedirectSearch } from "@/lib/routing/auth-return";

export interface RouteGuardResult {
  onboardingStatus?: OnboardingStatusResponse;
}

async function loadOnboardingStatus(): Promise<OnboardingStatusResponse> {
  const response = await readOnboardingStatusApiOnboardingStatusGet();

  if (response.status !== 200) {
    const errorBody =
      typeof response.data === "string" ? response.data : JSON.stringify(response.data);
    throw new Error(
      JSON.stringify({
        detail: "Failed to get onboarding status",
        status: response.status,
        body: errorBody,
      }),
    );
  }

  return response.data as OnboardingStatusResponse;
}

/**
 * Input parameters for loader status function
 */
interface LoaderStatusInput {
  pathname: string;
}

/**
 * Fast authentication check for beforeLoad
 *
 * This function only checks if the user is authenticated with Clerk.
 * It does NOT load onboarding status or execute state machine redirects.
 *
 * Purpose:
 * - Quick authentication check (fail-fast pattern)
 * - Used in beforeLoad to prevent unauthorized access
 * - Minimal overhead (~10ms) vs full route guard (~200ms)
 *
 * Usage:
 * ```ts
 * export const Route = createFileRoute("/workspace/$workspaceSlug")({
 *   beforeLoad: () => routeAuthCheck(),
 *   loader: async ({ location }) => {
 *     // Load data and execute state machine here
 *   }
 * });
 * ```
 *
 * @returns Empty object (no data needed for authentication check)
 */
export const routeAuthCheck = createServerFn({ method: "GET" })
  .validator(() => ({}))
  .handler(async () => {
    const request = getWebRequest();
    const auth = await getAuth(request);

    // Only check authentication status
    if (!auth.isAuthenticated) {
      logInfo("route.guard.unauthenticated", {
        message: "User not authenticated, redirecting to signin",
      });
      throw redirect({
        to: "/signin",
        search: buildSigninRedirectSearch(request.url),
      });
    }

    logInfo("route.guard.auth_passed", {
      message: "Authentication check passed",
    });

    return {}; // No data to return
  });

/**
 * Load onboarding status for beforeLoad context
 *
 * This function loads onboarding status WITHOUT executing redirects.
 * It's designed for use in beforeLoad to populate route context with
 * onboarding data that child routes can access.
 *
 * Purpose:
 * - Load onboarding status into route context (child routes need this)
 * - Authentication check (fail-fast)
 * - NO state machine redirects (to avoid interrupting stale-while-revalidate)
 *
 * The state machine redirects are executed in the loader, which allows:
 * - Redirects don't interrupt stale-while-revalidate
 * - User sees cached data immediately
 * - Background refresh happens without navigation interruption
 *
 * Usage:
 * ```ts
 * export const Route = createFileRoute("/workspace/$workspaceSlug")({
 *   beforeLoad: () => loadOnboardingStatusForContext(),
 *   loader: async ({ location, context }) => {
 *     // Execute state machine redirects here
 *     const target = resolveRedirectTargetEnhanced(context.onboardingStatus, location.pathname);
 *     if (target) throw redirect(target);
 *   }
 * });
 * ```
 *
 * @returns Object with onboardingStatus for route context
 */
export const loadOnboardingStatusForContext = createServerFn({ method: "GET" })
  .validator(() => ({}))
  .handler(async () => {
    const request = getWebRequest();
    const auth = await getAuth(request);

    // Authentication check
    if (!auth.isAuthenticated) {
      logInfo("route.guard.unauthenticated", {
        message: "User not authenticated, redirecting to signin",
      });
      throw redirect({
        to: "/signin",
        search: buildSigninRedirectSearch(request.url),
      });
    }

    let status: OnboardingStatusResponse;

    try {
      status = await loadOnboardingStatus();
    } catch (error) {
      // Handle authentication failures (401/403)
      if (isAuthError(error)) {
        const errorStatus = extractApiErrorStatus(error);
        logWarn("auth.route_guard.token_expired", {
          function: "loadOnboardingStatusForContext",
          status: errorStatus,
        });
        throw redirect({
          to: "/signin/expired",
          search: { reason: "token_expired" },
        });
      }

      // Re-throw other errors
      throw error;
    }

    logInfo("route.context.status_loaded", {
      onboarding_state: status.onboarding_state,
      workspace_slug: status.workspace_slug,
      workspace_id: status.workspace_id,
    });

    // Return status to context (NO redirects here)
    return { onboardingStatus: status };
  });

/**
 * Load onboarding status and execute state machine redirects
 *
 * This function should be used in LOADERS (not beforeLoad) to:
 * 1. Load user's onboarding status from API
 * 2. Execute state machine redirects based on current state
 * 3. Return onboarding status for use in the route
 *
 * Purpose:
 * - Separate data loading from authentication checks
 * - Enable true "stale-while-revalidate" behavior
 * - Loader redirects don't interrupt background refresh
 *
 * Usage:
 * ```ts
 * export const Route = createFileRoute("/workspace/$workspaceSlug")({
 *   beforeLoad: () => routeAuthCheck(),
 *   loader: async ({ location }) => {
 *     const { onboardingStatus } = await loadOnboardingStatusAndRedirect({
 *       data: { pathname: location.pathname },
 *     });
 *     return { onboardingStatus };
 *   }
 * });
 * ```
 *
 * @param data.pathname - Current pathname for state machine evaluation
 * @returns Object with onboardingStatus
 */
export const loadOnboardingStatusAndRedirect = createServerFn({ method: "GET" })
  .validator((data: LoaderStatusInput) => data)
  .handler(async ({ data }) => {
    const { pathname } = data;

    let status: OnboardingStatusResponse;

    try {
      status = await loadOnboardingStatus();
    } catch (error) {
      // Handle authentication failures (401/403)
      if (isAuthError(error)) {
        const errorStatus = extractApiErrorStatus(error);
        logWarn("auth.route_guard.token_expired", {
          function: "loadOnboardingStatusAndRedirect",
          status: errorStatus,
          pathname,
        });
        throw redirect({
          to: "/signin/expired",
          search: { reason: "token_expired" },
        });
      }

      // Re-throw other errors
      throw error;
    }

    logInfo("route.loader.status_loaded", {
      pathname,
      onboarding_state: status.onboarding_state,
      workspace_slug: status.workspace_slug,
      workspace_id: status.workspace_id,
    });

    // Execute state machine redirects
    executeStateMachineRedirect(status, pathname, "route.loader");

    return { onboardingStatus: status };
  });
